import { Injectable } from '@angular/core';
import { LatLonLocation, NodeData } from 'src/app/interfaces/node-models.interface';
import { BehaviorSubject, EMPTY, Observable, timer } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import { CcAdminApiService } from '../ccAdmin/cc-admin-api.service';
import { ApiService } from '../api/api.service';
import { toAction, toTrigger } from '../workspace/workspace.utils';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { SidenavEntry } from 'src/app/interfaces/workspace.interface';
import { hasExceededThreshold } from 'src/app/components/threshold-settings-editor/threshold.utils';
import { Toast, ToastButton, ToastService } from '@msi/cobalt';
import { BasicModalService } from '../basic-modal/basic-modal.service';
import { ModalData } from 'src/app/interfaces/modal.interface';
import { modalConfig } from '../basic-modal/basic-modal-configs.const';
import { notNull } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';
import { Router } from '@angular/router';
import { ThresholdContainingProduct } from 'src/app/interfaces/threshold-settings.interface';
import { nodesAlphabetically } from 'src/app/utils/sorters/sorters.utils';
import { TimeSincePipe } from 'src/app/pipes/time-since/time-since.pipe';

@Injectable({
	providedIn: 'root',
})
export class SidetrayDataService {
	get allSidetrayNodes(): Map<ObjID, NodeData> {
		const allNodes = new Map<ObjID, NodeData>();
		for (const node of this.allTriggers.concat(this.allActions)) {
			allNodes.set(node.vertexId, node);
		}
		return allNodes;
	}

	sharedSelect = new BehaviorSubject<SidenavEntry>(null);
	currentSelection: SidenavEntry;
	allTriggers: NodeData[] = [];
	allActions: NodeData[] = [];
	stackSidenavAndSidetray = false;
	private defaultApps: Map<string, SidenavEntry> = new Map([]);
	private currentApps: Map<string, SidenavEntry> = cloneDeep(this.defaultApps);

	private productErrorToastMap: Map<string, Toast> = new Map();

	// Subjects to alert sidenav of changes during/after HTTP GET requests
	private sidetrayLoadedSubject = new BehaviorSubject<Map<string, SidenavEntry>>(null);
	public get sidetrayLoaded$(): Observable<Map<string, SidenavEntry>> {
		return this.sidetrayLoadedSubject.asObservable().pipe(notNull());
	}

	constructor(
		private apiService: ApiService,
		private ccAdminApiService: CcAdminApiService,
		private loadingErrorsService: LoadingErrorsService,
		private toastService: ToastService,
		private basicModalService: BasicModalService,
		private router: Router
	) {
		// Once graph is initialized, perform initial load of actions/triggers
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			// Reload sidetray contents every 5 minutes
			timer(0, 300000)
				.pipe()
				.subscribe(() => {
					this.reloadSidetray();
				});
		});
	}

	public setSelectedSidetray(appSelected: SidenavEntry): boolean {
		this.sharedSelect.next(appSelected);
		return true;
	}

	public reloadSidetray() {
		// Get all triggers and actions
		this.currentApps = cloneDeep(this.defaultApps);
		this.loadSidetray();
	}

	public renameSidetrayNode(vId: ObjID, name: string) {
		// Rename trigger from sidetray
		for (const trigger of this.allTriggers || []) {
			if (trigger.vertexId === vId) {
				trigger.friendlyName = name;
			}
		}
		// Rename action from sidetray
		for (const action of this.allActions || []) {
			if (action.vertexId === vId) {
				action.friendlyName = name;
			}
		}
		// Emit updates to refresh sidetray contents
		this.allTriggers = this.allTriggers.sort(nodesAlphabetically);
		this.allActions = this.allActions.sort(nodesAlphabetically);
		this.sidetrayLoadedSubject.next(this.currentApps);
	}

	public updateIsCriticalSidetrayNode(vId: ObjID, isCritical: boolean) {
		// Update trigger from sidetray
		for (const trigger of this.allTriggers || []) {
			if (trigger.vertexId === vId) {
				trigger.isCritical = isCritical;
			}
		}
		// Emit updates to refresh sidetray contents
		this.allTriggers = this.allTriggers.sort(nodesAlphabetically);
		this.sidetrayLoadedSubject.next(this.currentApps);
	}

	// Get a copy of a product's information
	public getProductInfo(productId: string): SidenavEntry {
		return cloneDeep(this.currentApps.get(productId));
	}

	// Update the threshold information for a product
	public updateProductThresholdInfo(p: ThresholdContainingProduct) {
		const product = this.currentApps.get(p.id);
		product.thresholdConfig = p.thresholdConfigNew;

		// Recheck if threshold has been exceeded and update if needed
		this.checkIfProductHasExceededThreshold(product.id);
		this.sidetrayLoadedSubject.next(this.currentApps);
	}

	// Opens a modal indicating that a product's threshold has been exceeded
	public openProductThresholdExceededModal(productId: string) {
		const productInfo = this.currentApps.get(productId);
		const timeUnits =
			productInfo.thresholdConfig.timeNumber === 1
				? productInfo.thresholdConfig.timeUnits.slice(0, -1)
				: productInfo.thresholdConfig.timeUnits;
		const modalData: ModalData = {
			...modalConfig.thresholdExceeded,
			message: $localize`
        Timeout threshold for "${productInfo.displayName}" has been exceeded.
        The current threshold is set to ${productInfo.thresholdConfig.timeNumber} ${timeUnits}.
        "${productInfo.displayName}" was last heard from ${new TimeSincePipe().transform(productInfo.lastHeardFrom)}.
        You may want to check the status of "${productInfo.displayName}" or contact administrator support.`,
		};
		this.basicModalService.createModal(modalData).subscribe(() => {
			this.router.navigateByUrl('/settings/orch');
		});
	}

	// Load all sidetray nodes
	private loadSidetray() {
		this.apiService
			.getSidetray()
			.pipe(
				catchError(err => {
					// Go to error page if initial sidetray load fails
					if (this.allTriggers.length === 0 && this.allActions.length === 0) {
						this.loadingErrorsService.openErrorPage(
							err?.status?.toString(),
							err?.statusText,
							$localize`Failed to load source nodes. If issues persist, contact your system administrator.`
						);
					}
					return EMPTY;
				})
			)
			.subscribe({
				next: products => {
					const actions = [];
					const triggers = [];

					for (const product of products) {
						// Load list of apps
						this.currentApps.set(product.id, {
							...product,
							displayName: product.name ?? product.id,
							enabled:
								product.triggers.filter(t => !t.ttl).length > 0 || product.actions.filter(a => !a.ttl).length > 0,
							hasTriggers: product.triggers.length > 0,
						});

						// Check if product has exceeded its lastHeardFrom threshold
						this.checkIfProductHasExceededThreshold(product.id);

						// Load triggers
						for (const trigger of product.triggers || []) {
							triggers.push(toTrigger(trigger, product));
						}

						// Load actions
						for (const action of product.actions || []) {
							actions.push(toAction(action, product));
						}
					}

					// Update the public lists of triggers, actions, and apps
					this.allTriggers = triggers.sort(nodesAlphabetically);
					this.allActions = actions.sort(nodesAlphabetically);
					this.sidetrayLoadedSubject.next(this.currentApps);
				},
			});
	}

	// Check if product has exceeded its threshold and display information accordingly
	private checkIfProductHasExceededThreshold(productId: string) {
		if (hasExceededThreshold(this.currentApps.get(productId))) {
			this.thresholdHasBeenExceededFor(productId);
		} else {
			// Mark as not exceeded
			const product = this.currentApps.get(productId);
			product.thresholdExceeded = false;
			// Dismiss error toast, if exists and display success toast if threshold is enabled
			if (this.productErrorToastMap.has(productId)) {
				this.toastService.hideToast(this.productErrorToastMap.get(productId));
				this.productErrorToastMap.delete(productId);
				if (product.thresholdConfig.enabled) {
					this.toastService.success($localize`Threshold for ${product.displayName} has been reestablished.`, null, {
						autoDismiss: 7000,
					});
				}
			}
		}
	}

	// Update product thresholdExceeded state and display error toasts
	// (assuming threshold is still exceeded) Sidetray reloads will cause the error toast to close and re-open
	// Unfortunately, this is unavoidable as Cobalt does not provide a mechanism to determine if a Toast has been closed
	private thresholdHasBeenExceededFor(productId: string) {
		// Mark as exceeded
		const product = this.currentApps.get(productId);
		product.thresholdExceeded = true;
		// Init toast configuration
		const toastButton: ToastButton = {
			text: $localize`More info`,
			click: () => {
				this.openProductThresholdExceededModal(productId);
			},
		};
		// Close error toast if exists
		if (this.productErrorToastMap.has(productId)) {
			this.toastService.hideToast(this.productErrorToastMap.get(productId));
			this.productErrorToastMap.delete(productId);
		}
		// Display new error toast and track it
		const newToast = this.toastService.warning(
			$localize`Timeout threshold for ${product.displayName} has been exceeded. Click for more info.`,
			toastButton
		);
		this.productErrorToastMap.set(productId, newToast);
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
